import React, { useEffect } from "react";
import ReactHtmlParser from "react-html-parser";
import "./index.css";
import Layout from "@herb/layouts";
import SEO from "@herb/components/seo";
import { Container } from "@herb/ui";
import { deployUrl } from "@herb/utils";
import { graphql } from "gatsby";
import TypeFormEmbed from "@herb/components/TypeFormEmbed";
import Slider from "react-slick";
import AnnoucementBarHempBan from "../../components/AnnoucementBarHempBan";

const CustomPrevArrow = props => {
  const { className, style, onClick } = props;
  return (
    <div
      className={`${className} cwh-arrow cwh-arrow-prev`}
      // style={style}
      onClick={onClick}
      role="button"
      tabIndex={0}
      important
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15 19l-7-7 7-7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

const CustomNextArrow = props => {
  const { className, style, onClick } = props;
  return (
    <div
      className={`${className} cwh-arrow cwh-arrow-next`}
      // style={style}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 5l7 7-7 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

const CreateWithHerb = ({ data }) => {
  const [sliderKey, setSliderKey] = React.useState("ssr");
  const { wordpressWpPage: createWithHerb } = data;
  const { title, content, slug, contentsCreateWithHerb } = createWithHerb;
  const breadcrumbs = [
    {
      name: "Home",
      url: "/"
    },
    {
      name: title,
      url: `/${slug}`
    }
  ];

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      setSliderKey("client");
      window.dispatchEvent(new Event("resize"));
    });

    return () => window.cancelAnimationFrame(rafId);
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    swipeToSlide: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: "80px",
          rows: 1
        }
      }
    ]
  };

  return (
    <Layout>
      <SEO
        title={title}
        description={content}
        url={`/${slug}`}
        canonical={`${deployUrl}/${slug}`}
        breadcrumbs={breadcrumbs}
      />
      <AnnoucementBarHempBan />
      <Container flexDirection="column" alignItems="center" px={4}>
        <h1 className="cwh-title">{title}</h1>
        <div className="cwh-description">{ReactHtmlParser(content)}</div>
        {/* render typeform component */}
        <div className="cwh-typeform-container">
          <TypeFormEmbed formId={contentsCreateWithHerb?.typeformId} />
        </div>

        <div className="cwh-media-gallery">
          {contentsCreateWithHerb?.mediaLibrary?.length > 0 && (
            <Slider key={sliderKey} {...sliderSettings}>
              {contentsCreateWithHerb.mediaLibrary.map((mediaItem, index) => (
                <div
                  key={mediaItem?.id}
                  className={`cwh-media-card-wrapper item-index-${index}`}
                >
                  <MediaCard item={mediaItem} />
                </div>
              ))}
            </Slider>
          )}
        </div>
      </Container>
    </Layout>
  );
};
export default CreateWithHerb;

export const pageQuery = graphql`
  query {
    wordpressWpPage(slug: { eq: "create-with-herb" }) {
      title
      content
      slug
      contentsCreateWithHerb {
        typeformId
        mediaLibrary {
          mediaItem {
            image {
              altText
              sourceUrl
            }
            video {
              altText
              mediaItemUrl
            }
            link
          }
        }
      }
    }
  }
`;

const MediaCard = ({ item }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const { image, video, link } = item.mediaItem || {};

  const hasImage = !!image?.sourceUrl;
  const hasVideo = !!video?.mediaItemUrl;

  const dragStartPos = React.useRef(null);

  const handleMouseDown = e => {
    dragStartPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleClick = e => {
    if (dragStartPos.current) {
      const dx = Math.abs(e.clientX - dragStartPos.current.x);
      const dy = Math.abs(e.clientY - dragStartPos.current.y);
      // If cursor moved more than 5 pixels, treat it as a drag/swipe and ignore click
      if (dx > 5 || dy > 5) {
        e.preventDefault();
        return;
      }
    }

    if (hasVideo) {
      setIsPlaying(true);
    } else if (link) {
      window.open(link, "_blank");
    }
  };

  if (!hasImage && hasVideo) {
    return (
      <div className="cwh-media-card">
        <video src={video.mediaItemUrl} controls className="cwh-media-video" />
      </div>
    );
  }

  return (
    <div
      className="cwh-media-card"
      onMouseDown={handleMouseDown}
      onClick={!isPlaying ? handleClick : undefined}
    >
      {isPlaying && hasVideo ? (
        <video
          src={video.mediaItemUrl}
          autoPlay
          controls
          className="cwh-media-video"
        />
      ) : hasImage ? (
        <img
          src={image.sourceUrl}
          alt={image.altText || "Media item"}
          className="cwh-media-image"
        />
      ) : null}
    </div>
  );
};
